import capitalize from "lodash/capitalize";
import { useCallback, useEffect, useRef } from "react";

import { getGeocode, useOrderMutation, useOrdersCache } from "@web-app/api";
import { useAlert, validatePhone } from "@web-app/components";
import {
  FieldValue,
  GoogleAddress,
  LocationDetails,
  Order,
  OrderStatus,
  OrderStatusProgress,
  TransportType,
  User,
} from "@web-app/types";

import OrderEditPanel from "./OrderDetailsPanel/OrderEditPanel";

interface OrderEditPanelWrapperProps {
  onClose: () => void;
  order: Order;
}

/**
 * Wrapper for OrderEditPanel used in Dispatch Page
 * Handles dispatch-specific cache management and update logic
 */
export default function OrderEditPanelWrapper({
  order,
  onClose,
}: OrderEditPanelWrapperProps) {
  const { updateOrder } = useOrderMutation();
  const { showAlertAsync, showAlert } = useAlert();
  const ordersCache = useOrdersCache();

  // Use ref to keep the latest order without causing handleFieldChange to recreate
  const orderRef = useRef(order);

  useEffect(() => {
    orderRef.current = order;
  }, [order]);

  const getValidationError = (
    property: string,
    value: FieldValue,
    order: Order,
    fromTab: string,
  ) => {
    if (
      !value &&
      ["name", "phone", "address", "DateTime"].some((prop) =>
        property.includes(prop),
      )
    ) {
      return "Required fields cannot be empty";
    }

    if (
      (property === "scheduledStartDateTime" &&
        new Date(order.scheduledEndDateTime).getTime() <=
          new Date(value as string).getTime()) ||
      (property === "scheduledEndDateTime" &&
        new Date(value as string).getTime() <=
          new Date(order.scheduledStartDateTime).getTime())
    ) {
      return `Please ensure '${capitalize(fromTab)} Before' is later than '${capitalize(fromTab)} After'`;
    }

    if (property.includes("phone") && !validatePhone(value as string)) {
      return "Please enter a valid phone number (e.g. +1234567890)";
    }

    return "";
  };

  const handleFieldChange = useCallback(
    async (
      property: string,
      value: FieldValue,
      fromTab?: "dropoff" | "pickup",
    ) => {
      const order = orderRef.current;
      let payload = {} as Partial<Order>;

      const isPropertyAddress =
        property === "pickup.address" || property === "dropoff.address";
      const googleAddress = value as GoogleAddress | null;
      let geocodedAddressDetails: Partial<LocationDetails> | undefined;
      let geocodedLocation:
        | {
            coordinates: number[];
            type: string;
          }
        | undefined;

      if (isPropertyAddress && googleAddress?.description) {
        try {
          const geocode = await getGeocode(googleAddress.description);
          geocodedAddressDetails = {
            adminArea2: geocode.adminArea2,
            country: geocode.country,
            extendedAddress: geocode.extendedAddress,
            locality: geocode.locality,
            postalCode: geocode.postalCode,
            state: geocode.state,
            streetAddress: geocode.streetAddress,
          };
          geocodedLocation = {
            coordinates: [geocode.lng, geocode.lat],
            type: "Point",
          };
        } catch {
          // backend will geocode from address string
        }
      }

      if (!order || !property) {
        return;
      }

      const updatedOrder = structuredClone(order);

      if (property.startsWith("status")) {
        const [status, statusProgress] = (value as string).split("_");

        updatedOrder.status = status as OrderStatus;
        payload.status = status as OrderStatus;

        if (statusProgress) {
          if (status === "INPROGRESS") {
            const transitTo =
              statusProgress === "PICKUP"
                ? " to Pickup"
                : statusProgress === "DROPOFF"
                  ? " to Drop-off"
                  : "";
            updatedOrder.statusText = `In Transit${transitTo}`;
          }

          updatedOrder.statusProgress = statusProgress as OrderStatusProgress;
          payload.statusProgress = statusProgress as OrderStatusProgress;
        }
      } else if (property === "assignee") {
        updatedOrder.assigneeId = value as string;
        payload.assigneeId = value as string;
      } else if (property === "minVehicle") {
        updatedOrder.minVehicle = value as TransportType;
        payload.minVehicle = value as TransportType;
      } else if (isPropertyAddress) {
        const locationKey =
          property === "pickup.address" ? "pickup" : "dropoff";
        if (googleAddress) {
          const locationDetails = {
            ...updatedOrder[locationKey],
            ...geocodedAddressDetails,
            address: googleAddress.description,
          };
          payload[locationKey] = locationDetails;
          updatedOrder[locationKey] = locationDetails;
        }
      } else {
        const [type, prop] = property.includes(".")
          ? property.split(".")
          : [undefined, property];

        if (type) {
          payload = {
            ...payload,
            [type]: {
              [prop]: value,
            },
          };

          // @ts-ignore typed any
          updatedOrder[type][prop] = value;
        } else {
          payload = {
            ...payload,
            [prop]: prop.includes("DateTime")
              ? new Date(value as string)
              : value,
          };
          // @ts-ignore typed any
          updatedOrder[prop] = value;
        }
      }

      const errorMsg = getValidationError(
        property,
        value,
        updatedOrder,
        fromTab ?? "",
      );

      if (errorMsg) {
        showAlert({
          title: errorMsg,
          type: "error",
        });
        return;
      }

      ordersCache.updateCachedOrders((data) => {
        const orders = data?.orders;
        if (!orders) {
          return data;
        }
        const clonedOrders = structuredClone(orders);
        if (!clonedOrders.has(order.id)) {
          return data;
        }
        clonedOrders.set(order.id, updatedOrder);
        return {
          apps: data.apps,
          assignees: data.assignees || new Map<string, User>(),
          orders: clonedOrders,
        };
      });

      if (isPropertyAddress && geocodedLocation) {
        const locationField =
          property === "pickup.address" ? "pickupLocation" : "dropoffLocation";
        payload[locationField] = geocodedLocation;
      }

      await showAlertAsync(
        updateOrder.mutateAsync({
          ...payload,
          id: order.id,
        }),
        {
          error: {
            title: "Something went wrong, please try again!",
            type: "error",
          },
          loading: { title: "Updating Order ...", type: "default" },
          success: {
            title: "Successfully Updated Order",
            type: "success",
          },
        },
      );
      // Note: The mutation's onSuccess already handles cache invalidation
    },
    [ordersCache, showAlert, showAlertAsync, updateOrder],
  );

  return (
    <OrderEditPanel
      order={order}
      onClose={onClose}
      onFieldChange={handleFieldChange}
    />
  );
}
